import dayjs from 'dayjs';
import { KioskConnectivityStatusBarProps } from '../../ui/components/pure/kiosk-connectivity/kiosk-connectivity-status-bar/kiosk-connectivity-status-bar.component';
import {
  InternalKioskConnectivityUpdate,
  UseKioskConnectivityStatusHookResult
} from './kiosk-connectivity-status-bar.hook';
import { KnownKioskDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';

export function kioskConnectivityStatusUpdatesToStatusBarProps({
  updates,
  untouchedKioskIDs,
  kiosks,
  deactivateAlert
}: UseKioskConnectivityStatusHookResult): KioskConnectivityStatusBarProps {
  const now = new Date();

  function getHubName(iotHubID: string) {
    return kiosks.find((k) => k.iotHubID === iotHubID)?.hubName || 'Unknown hub';
  }
  function getTimeframeFromDiff(a: Date, b?: Date | string) {
    if (!b) {
      return 0;
    }
    const diff = Math.abs(dayjs(a).diff(b));
    return getTimeframe(diff);
  }
  function getTimeframe(milliseconds: number) {
    const minutes = Math.floor(milliseconds / 60 / 1000);
    if (minutes < 60) {
      return `${minutes.toFixed()} minute${minutes > 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours.toFixed()} hour${hours > 1 ? 's' : ''}`;
    }
    const days = Math.floor(hours / 24);
    return `${days.toFixed()} day${days > 1 ? 's' : ''}`;
  }
  function getListItemMessage(update: InternalKioskConnectivityUpdate) {
    if (update.status === KnownKioskDtoStatus.Inactive) {
      return undefined;
    }
    if (!update.lastConnectedTime) {
      return undefined;
    }
    return `offline for ${getTimeframeFromDiff(now, update.lastConnectedTime)}`;
  }
  function getStatusBarMessage(numTotalKiosks: number, numKiosksInThisStatus: number, status: 'offline' | 'online') {
    if (numTotalKiosks === 1) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
    if (status === 'online') {
      return `All hubs ${status}`;
    }
    return `${numKiosksInThisStatus} hub${numKiosksInThisStatus > 1 ? 's' : ''} ${status}`;
  }
  function sortUpdates(updates: InternalKioskConnectivityUpdate[]) {
    return updates.slice().sort((a, b) => {
      if (!b.lastConnectedTime) {
        return -1;
      }
      if (!a.lastConnectedTime) {
        return 1;
      }
      return new Date(b.lastConnectedTime).getTime() - new Date(a.lastConnectedTime).getTime();
    });
  }
  const props: KioskConnectivityStatusBarProps = {
    statuses: []
  };
  let greenIdx = -1;
  let orangeIdx = -1;
  let redIdx = -1;
  let greyIdx = -1;

  if (updates.grey.length > 0) {
    greyIdx = 0;
    props.statuses.push({
      hubs: sortUpdates(updates.grey).map((u) => ({
        name: getHubName(u.iotHubID)
      })),
      color: 'grey',
      pulse: false,
      onOpen: () => {}
    });
  }
  if (updates.green.length > 0) {
    greenIdx = greyIdx + 1;
    props.statuses.push({
      hubs: sortUpdates(updates.green).map((u) => ({
        name: getHubName(u.iotHubID)
      })),
      color: 'green',
      pulse: updates.green.some((u) => untouchedKioskIDs.includes(u.iotHubID)),
      onOpen: () => deactivateAlert(updates.green.map((u) => u.iotHubID))
    });
  }
  if (updates.orange.length > 0) {
    orangeIdx = greenIdx + 1;
    props.statuses.push({
      hubs: sortUpdates(updates.orange).map((u) => ({
        name: getHubName(u.iotHubID),
        message: getListItemMessage(u)
      })),
      color: 'orange',
      pulse: updates.orange.some((u) => untouchedKioskIDs.includes(u.iotHubID)),
      onOpen: () => deactivateAlert(updates.orange.map((u) => u.iotHubID))
    });
  }
  if (updates.red.length > 0) {
    redIdx = orangeIdx > -1 ? orangeIdx + 1 : greenIdx + 1;
    props.statuses.push({
      hubs: sortUpdates(updates.red).map((u) => ({
        name: getHubName(u.iotHubID),
        message: getListItemMessage(u)
      })),
      color: 'red',
      pulse: updates.orange.some((u) => untouchedKioskIDs.includes(u.iotHubID)),
      onOpen: () => deactivateAlert(updates.red.map((u) => u.iotHubID))
    });
  }
  props.message =
    redIdx > -1
      ? getStatusBarMessage(kiosks.length, props.statuses[redIdx].hubs.length, 'offline')
      : orangeIdx > -1
      ? getStatusBarMessage(kiosks.length, props.statuses[orangeIdx].hubs.length, 'offline')
      : greenIdx > -1
      ? getStatusBarMessage(kiosks.length, props.statuses[greenIdx].hubs.length, 'online')
      : 'No hubs';
  return props;
}
